import React, {useEffect, useState, useRef} from "react";
import {useLocation, Link} from "react-router-dom";
import ShareButton from "./ShareButton";
import {
  Container,
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  ButtonGroup,
  Badge,
  Tooltip,
} from "reactstrap";
import {ChartSkeleton} from "./SkeletonLoader";
import {
  Chart as ChartJS,
  Plugin,
  ScriptableContext,
  TooltipItem,
} from "chart.js";
import {Line} from "react-chartjs-2";
import _ from "lodash";
import {formatDate} from "../utils/dates";
import {displaySymbol} from "../utils/delisted";
import {buildDashboardUrl} from "../utils/links";
import {PRODUCTION_MIN_SCORE} from "../utils/windows";
import {useTheme} from "../context/ThemeContext";
import posthog from "posthog-js";

import {ViewFeaturedProps} from "../containers/ViewFeatured";
import EarningsBadge from "./EarningsBadge";
import FundamentalsChip from "./FundamentalsChip";
import ModernStockChart from "./ModernStockChart";
import { useSeo } from "../hooks/useSeo";

interface Props {
  groups: MatchesState[];
  label: string;
  window: number;
  forecastWindow: number;
  viewType: "normalized" | "percentage" | "price";
  isDarkMode: boolean;
  showProjection: boolean;
  chartRef?: React.RefObject<ChartJS<"line"> | null>;
}

// StockChartGroup component modernized version
const StockChartGroup = ({groups, label, window, forecastWindow, viewType = "normalized", isDarkMode, showProjection, chartRef}: Props) => {
  // Theme-aware colors
  const textColor = isDarkMode ? "#F1F5F9" : "#0F172A";
  const textMutedColor = isDarkMode ? "#94A3B8" : "#64748B";
  const gridColor = isDarkMode ? "rgba(148, 163, 184, 0.5)" : "rgba(203, 213, 225, 0.5)";

  const [hiddenDatasets, setHiddenDatasets] = useState(
    new Set(
      groups
        .slice(6)
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        .filter((v) => v)
        .map((v) => v.symbol),
    ),
  );

  const colors = [
    "#3B82F6", // primary blue
    "#059669", // green
    "#DC2626", // red
    "#8B5CF6", // purple
    "#F97316", // orange
    "#0891B2", // cyan
    "#10B981", // emerald
    "#84CC16", // lime
    "#EC4899", // pink
    "#6366F1", // indigo
    "#FBBF24", // amber
    "#7C3AED", // violet
    "#F43F5E", // rose
    "#14B8A6", // teal
    "#D946EF", // fuchsia
    "#22D3EE", // sky
    "#525252", // gray
    "#78350F", // brown
  ];

  const xlabels = window + forecastWindow;

  const getPercentageChange = (prices: number[]) =>
    prices.map((price) => ((price - prices[window - 1]) / prices[window - 1]) * 100);

  // Add toggle handler
  const handleDatasetToggle = (symbol: string) => {
    setHiddenDatasets((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(symbol)) {
        newSet.delete(symbol);
      } else {
        newSet.add(symbol);
      }
      return newSet;
    });
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: "Day",
          color: textMutedColor,
          font: {
            family: "Inter",
            size: 12,
            weight: 500,
          },
        },
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textMutedColor,
          font: {
            family: "IBM Plex Mono",
            size: 10,
          },
        },
      },
      y: {
        display: true,
        title: {
          display: false,
          text: viewType === "percentage" ? "% Change" : viewType === "price" ? "Price ($)" : "Normalized Value",
          color: textMutedColor,
          font: {
            family: "Inter",
            size: 12,
            weight: 500,
          },
        },
        grid: {
          color: gridColor,
        },
        ticks: {
          color: textMutedColor,
          font: {
            family: "IBM Plex Mono",
            size: 10,
          },
          callback:
            viewType === "price"
              ? (value: string | number) => `$${Number(value).toFixed(2)}`
              : undefined,
        },
      },
    },
    plugins: {
      tooltip: {
        mode: "index" as const,
        intersect: false,
        // Hide the invisible projection band datasets (blank label) from the tooltip.
        filter: (item: TooltipItem<"line">) => (item.dataset.label ?? "") !== "",
        backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(15, 23, 42, 0.8)",
        titleColor: "#F1F5F9",
        bodyColor: "#F1F5F9",
        titleFont: {
          family: "Inter",
          size: 14,
          weight: "bold" as const,
        },
        bodyFont: {
          family: "Inter",
          size: 12,
        },
        padding: 10,
        cornerRadius: 4,
        callbacks: {
          label: function (context: TooltipItem<"line">) {
            const label = context.dataset.label || "";
            if (label) {
              const dataPoint = context.raw as YAxisData;
              if (dataPoint && dataPoint.ohlcv) {
                return `${label}: ${(dataPoint.ohlcv[4] as number).toFixed(2)} (${dataPoint.ohlcv[0]})`;
              }
              return `${label}: ${context.formattedValue}`;
            }
            return "";
          },
        },
      },
      title: {
        display: true,
        text: label,
        font: {
          family: "Inter",
          size: 18,
          weight: "bold" as const,
        },
        color: textColor,
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      legend: {
        display: false, // Hide default legend, we'll create custom one
      },
    },
    elements: {
      line: {
        tension: 0.3,
      },
      point: {
        // Dots hidden by default (lines only); they reappear on hover via hoverRadius.
        radius: 0,
        hoverRadius: (ctx: ScriptableContext<"line">) => {
          if (ctx.datasetIndex === 0) return 6;
          return 4;
        },
      },
    },
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
  };

  const primaryCloses = groups[0]?.ohlcv.map((ohlcv) => ohlcv[4]);

  // ---- Forward projection of the primary security from the analog matches ----
  // Each analog's ohlcv spans window + forecastWindow closes; its forward return at
  // horizon h (1..forecastWindow) is close[window-1+h] / close[window-1] - 1. We aggregate
  // those returns across ALL analogs into a median path plus an interquartile (25-75th pct)
  // cone, then anchor them to the primary's last actual price ("today", index window-1) so
  // the chart continues the primary line into the forecast window — showing where
  // analogs-like-this tended to go next. Purely a visualization of the matches already on
  // the chart; no extra data is fetched.
  const quantile = (sorted: number[], q: number): number => {
    if (sorted.length === 1) return sorted[0];
    const pos = (sorted.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
  };

  const analogCloses: number[][] = groups
    .slice(1)
    .map((g) => g.ohlcv.map((o) => Number(o[4])))
    .filter((cs) => cs.length > window - 1 && Number.isFinite(cs[window - 1]) && cs[window - 1] > 0);

  const projection = (() => {
    if (!showProjection || forecastWindow <= 0 || analogCloses.length < 3) return null;
    const med: (number | null)[] = [];
    const lo: (number | null)[] = [];
    const hi: (number | null)[] = [];
    for (let h = 1; h <= forecastWindow; h++) {
      const rets: number[] = [];
      for (const cs of analogCloses) {
        const fwd = cs[window - 1 + h];
        if (Number.isFinite(fwd)) rets.push(fwd / cs[window - 1] - 1);
      }
      if (rets.length < 3) { med.push(null); lo.push(null); hi.push(null); continue; }
      rets.sort((a, b) => a - b);
      med.push(quantile(rets, 0.5));
      lo.push(quantile(rets, 0.1));
      hi.push(quantile(rets, 0.9));
    }
    return { med, lo, hi, nAnalogs: analogCloses.length };
  })();

  // Map an aggregate forward return into the chart's current display space, anchored to the
  // primary. In "% Change" view the primary is 0% at today, so the value is just the return.
  // In "Normalized" view we reproject prices into the primary's z-score frame:
  // projected_norm = norm_today + (price_today * ret) / std, where std is the slope of the
  // primary's (close vs. z-norm) line — derived from its own data, so it's independent of the
  // exact normalization convention.
  const primaryNormToday = Number(groups[0].yaxis[window - 1]);
  const primaryPriceToday = Number(primaryCloses[window - 1]);
  const normSlope = (() => {
    const xs = groups[0].yaxis.map(Number);
    const ys = primaryCloses.map(Number);
    const n = Math.min(xs.length, ys.length);
    if (n < 2) return 0;
    let mx = 0, my = 0;
    for (let i = 0; i < n; i++) { mx += xs[i]; my += ys[i]; }
    mx /= n; my /= n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) { num += (xs[i] - mx) * (ys[i] - my); den += (xs[i] - mx) ** 2; }
    return den !== 0 ? num / den : 0;
  })();
  const retToDisplay = (ret: number | null): number | null => {
    if (ret === null || !Number.isFinite(ret)) return null;
    if (viewType === "percentage") return ret * 100;
    if (viewType === "price") {
      return Number.isFinite(primaryPriceToday) ? primaryPriceToday * (1 + ret) : null;
    }
    if (normSlope === 0 || !Number.isFinite(primaryNormToday) || !Number.isFinite(primaryPriceToday)) return null;
    return primaryNormToday + (primaryPriceToday * ret) / normSlope;
  };
  const anchorDisplay = viewType === "percentage" ? 0 : viewType === "price" ? primaryPriceToday : primaryNormToday;
  // Full-length (window + forecastWindow) series: null before today, the anchor at today,
  // then the mapped projection for each forecast day — so it aligns index-for-index with the
  // primary/match datasets and connects to the primary's last point.
  const projSeries = (rets: (number | null)[]): (number | null)[] =>
    Array.from({length: xlabels}, (_, i) => {
      if (i < window - 1) return null;
      if (i === window - 1) return anchorDisplay;
      const h = i - (window - 1);
      return h <= rets.length ? retToDisplay(rets[h - 1]) : null;
    });

  // Cone = two invisible band lines (p25, p75) with a translucent fill between them; drawn
  // before the match lines so it sits behind them. The median line is appended last (on top).
  const projectionBands = projection
    ? [
      {
        label: "",
        data: projSeries(projection.lo),
        borderColor: "transparent",
        backgroundColor: "transparent",
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: false,
        tension: 0.2,
        spanGaps: false,
      },
      {
        label: "",
        data: projSeries(projection.hi),
        borderColor: "transparent",
        // --signature: this cone is the analog-matching payoff — the projected
        // outcome derived from historical matches — so it's kept visually
        // distinct from the primary query line (colors[0], also blue) rather
        // than reusing it.
        backgroundColor: isDarkMode ? "rgba(251,191,36,0.18)" : "rgba(245,158,11,0.14)",
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: "-1",
        tension: 0.2,
        spanGaps: false,
      },
    ]
    : [];
  const projectionMedian = projection
    ? [
      {
        label: "Projected (median analog)",
        data: projSeries(projection.med),
        borderColor: isDarkMode ? "#FBBF24" : "#F59E0B",
        backgroundColor: isDarkMode ? "#FBBF24" : "#F59E0B",
        borderWidth: 2.5,
        borderDash: [6, 4],
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.2,
        spanGaps: false,
      },
    ]
    : [];

  const lineData = {
    // Generate labels from 1 to window size
    labels: Array.from({length: xlabels}, (_, i) => i + 1),
    datasets: [
      // Primary dataset
      ...groups.slice(0, 1).map((g) => ({
        label: displaySymbol(g.symbol, g.delisted),
        hidden: false,
        fill: false,
        data: _.zip(
          g.xaxis,
          viewType === "percentage"
            ? getPercentageChange(primaryCloses)
            : viewType === "price"
              ? primaryCloses
              : g.yaxis,
          g.ohlcv,
        ).map(([x, y, ohlcv]) => ({
          x: Number(x) || 0,
          y: Number(y) || 0,
          ohlcv,
        })),
        borderColor: colors[0],
        backgroundColor: colors[0],
        borderWidth: 4,
        pointRadius: 0,
        tension: 0.1,
        pointBackgroundColor: colors[0],
        pointBorderColor: "#fff",
        pointBorderWidth: 1,
      })),
      // Analog forward-projection cone (behind the match lines)
      ...projectionBands,
      // Matching datasets — omitted entirely in Actual Price mode: analogs can trade at
      // very different price levels than the primary, so overlaying them on one shared $
      // axis isn't meaningful (the small-multiples grid below gives each its own $ axis
      // instead). The legend still lists them but disables selection — see the legend render.
      ...(viewType === "price"
        ? []
        : groups
          .filter((v) => !hiddenDatasets.has(v.symbol))
          .slice(1)
          .map((g, i) => {
            const matchingCloses = g.ohlcv.map((ohlcv) => ohlcv[4]);
            return {
              label: displaySymbol(g.symbol, g.delisted),
              data: _.zip(
                g.xaxis,
                viewType === "percentage" ? getPercentageChange(matchingCloses) : g.yaxis,
                g.ohlcv,
              ).map(([x, y, ohlcv]) => ({
                x: Number(x) || 0,
                y: Number(y) || 0,
                ohlcv,
              })),
              borderColor: colors[i + 1],
              backgroundColor: colors[i + 1],
              borderWidth: 1.5,
              pointRadius: 0,
              // Analog lines are solid across their whole span (historical + forward);
              // only the median projection line below stays dashed.
              tension: 0.1,
            };
          })),
      // Median projected path (dashed, on top)
      ...projectionMedian,
    ],
  };

  const visibleColors: Record<string, string> = groups.reduce(
    (acc, val) => {
      if (!hiddenDatasets.has(val.symbol)) {
        acc[val.symbol] = colors[Object.keys(acc).length];
      }
      return acc;
    },
    {} as Record<string, string>,
  );

  // Vertical "today" divider + faint forecast-region shade, drawn only when the projection
  // is shown. Self-contained inline plugin (no chartjs-plugin-annotation dependency). The x
  // position is computed from evenly-spaced category indices, so it's robust to scale internals.
  const forecastMarkerPlugin: Plugin<"line"> = {
    id: "forecast-marker",
    beforeDatasetsDraw(chart) {
      if (!showProjection || !projection || forecastWindow <= 0 || xlabels < 2) return;
      const area = chart.chartArea;
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!area) return;
      const frac = (window - 1) / (xlabels - 1);
      const x = area.left + frac * (area.right - area.left);
      if (!Number.isFinite(x)) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.fillStyle = isDarkMode ? "rgba(59,130,246,0.06)" : "rgba(59,130,246,0.045)";
      ctx.fillRect(x, area.top, area.right - x, area.bottom - area.top);
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.strokeStyle = isDarkMode ? "rgba(148,163,184,0.6)" : "rgba(100,116,139,0.5)";
      ctx.moveTo(x, area.top);
      ctx.lineTo(x, area.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = isDarkMode ? "#94A3B8" : "#64748B";
      ctx.font = "600 10px Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("  today · forecast →", x, area.top + 11);
      ctx.restore();
    },
  };

  return (
    <div className="stock-chart-container">
      <div className="chart-wrapper" style={{height: "450px", marginBottom: "20px"}}>
        <Line ref={chartRef} data={lineData} options={options} plugins={[forecastMarkerPlugin]} />
      </div>

      {showProjection && projection && (
        <div className="text-muted mb-2" style={{fontSize: 12, lineHeight: 1.45}}>
          <span style={{color: colors[0], fontWeight: 600}}>Dashed line</span> = median path of {projection.nAnalogs} historical
          analogs; shaded band = middle 80% of their outcomes (10th–90th percentile). A projection from
          past analogs, not a prediction or guarantee.
        </div>
      )}

      <div className="custom-legend">
        {groups.map((g, i) => {
          // Analog lines aren't plotted in Actual Price mode (see the dataset construction
          // above), so their legend entries are shown for reference but not selectable.
          const analogDisabled = i > 0 && viewType === "price";
          return (
            <div
              key={g.symbol}
              className={`legend-item ${
                analogDisabled ? "disabled" : hiddenDatasets.has(g.symbol) ? "inactive" : "active"
              }`}
              onClick={() => i > 0 && !analogDisabled && handleDatasetToggle(g.symbol)}
              title={analogDisabled ? "Not shown in Actual Price view — switch to Normalized or % Change to compare analog shapes" : undefined}
            >
              {!analogDisabled && !hiddenDatasets.has(g.symbol) && (
                <div
                  className="color-indicator"
                  style={{backgroundColor: visibleColors[g.symbol]}}
                ></div>
              )}
              <span className={`symbol ${i === 0 ? "primary" : ""}`}>
                {displaySymbol(g.symbol, g.delisted)}
                {i === 0 && (
                  <Badge color="primary" pill className="ms-2">
                    Primary
                  </Badge>
                )}
                {i > 0 && g.score !== undefined && (
                  <span className="score">
                    {g.score.toFixed(2)}
                    {g.ohlcv.length > 1 && (
                      <span
                        className={g.ohlcv[g.ohlcv.length - 1][4] >= g.ohlcv[g.ohlcv.length - forecastWindow - 1][4] ? "text-success" : "text-danger"}
                        style={{marginLeft: "4px"}}
                      >
                        {g.ohlcv[g.ohlcv.length - 1][4] >= g.ohlcv[g.ohlcv.length - forecastWindow - 1][4] ? "↑" : "↓"}
                      </span>
                    )}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {groups.some((g) => g.delisted) && (
        <div className="text-muted" style={{fontSize: 11, marginTop: 4}}>
          <strong>*</strong> delisted security — no longer trades; shown as a historical analog.
        </div>
      )}
    </div>
  );
};

// StockStats component
const StockStats = ({data}: {data: StockData}) => {
  return (
    <tr>
      <td className="text-center">{data.count_up}</td>
      <td className="text-center">{data.count_dn}</td>
      <td className="text-center text-success">+{data.max_up.toFixed(1)}%</td>
      <td className="text-center text-danger">{data.max_dn.toFixed(1)}%</td>
      <td className="text-center text-success">{data.up_over_down.toFixed(2)}</td>
      <td className="text-center text-danger">{data.down_over_up.toFixed(2)}</td>
      <td className="text-center text-success">{data.up_score.toFixed(2)}</td>
      <td className="text-center text-danger">{data.down_score.toFixed(2)}</td>
      <td className="text-center text-success">{data.upside_ratio.toFixed(2)}</td>
      <td className="text-center text-danger">{data.downside_ratio.toFixed(2)}</td>
      <td className="text-center text-success">{data.total_up_score.toFixed(2)}</td>
      <td className="text-center text-danger">{data.total_down_score.toFixed(2)}</td>
    </tr>
  );
};

// StockStatsStacked — mobile transpose of StockStats: one row per metric with its Up
// (green) and Down (red) value, so all six metrics are visible without side-scrolling.
const STAT_ROWS: { label: string; up: (d: StockData) => string; dn: (d: StockData) => string }[] = [
  { label: "Matches", up: (d) => `${d.count_up}`, dn: (d) => `${d.count_dn}` },
  { label: "Max Move", up: (d) => `+${d.max_up.toFixed(1)}%`, dn: (d) => `${d.max_dn.toFixed(1)}%` },
  { label: "Strength Ratio", up: (d) => d.up_over_down.toFixed(2), dn: (d) => d.down_over_up.toFixed(2) },
  { label: "Movement Score", up: (d) => d.up_score.toFixed(2), dn: (d) => d.down_score.toFixed(2) },
  { label: "Risk-Adjusted", up: (d) => d.upside_ratio.toFixed(2), dn: (d) => d.downside_ratio.toFixed(2) },
  { label: "Total Score", up: (d) => d.total_up_score.toFixed(2), dn: (d) => d.total_down_score.toFixed(2) },
];

const StockStatsStacked = ({ data }: { data: StockData }) => (
  <Table borderless size="sm" className="mb-0" style={{ fontSize: 13 }}>
    <thead>
      <tr className="text-muted" style={{ borderBottom: "1px solid var(--border-color)" }}>
        <th>Metric</th>
        <th className="text-end">Up</th>
        <th className="text-end">Down</th>
      </tr>
    </thead>
    <tbody>
      {STAT_ROWS.map((m) => (
        <tr key={m.label}>
          <td>{m.label}</td>
          <td className="text-end text-success">{m.up(data)}</td>
          <td className="text-end text-danger">{m.dn(data)}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

interface InfoHeaderProps {
  label: React.ReactNode;
  tooltip: React.ReactNode;
  id: string;
  colSpan?: number;
  className?: string;
}

const InfoHeader: React.FC<InfoHeaderProps> = ({
  label,
  tooltip,
  id,
  colSpan = 1,
  className = "",
}) => {
  const [open, setOpen] = useState(false);

  return (
    <th colSpan={colSpan} className={`text-center ${className}`} id={`${id}-icon`}>
      {label}
      <Tooltip
        placement="top"
        isOpen={open}
        target={`${id}-icon`}
        toggle={() => setOpen((v) => !v)}
      >
        {tooltip}
      </Tooltip>
    </th>
  );
};

// A small file-type badge icon (page + folded corner + a colored extension banner),
// in the style of a CSV/PNG file glyph. Dependency-free inline SVG.
function FileTypeIcon({ ext, color, size = 22 }: { ext: string; color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block" }} role="img" aria-label={`${ext} file`}>
      <path
        d="M5.5 2.5h7L18 7.7v11.8a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2V4.5a2 2 0 0 1 2-2z"
        fill="#fff" stroke="#94A3B8" strokeWidth="1.1"
      />
      <path d="M12.5 2.5v5.2H18" fill="none" stroke="#94A3B8" strokeWidth="1.1" />
      <rect x="1.5" y="12.5" width="19" height="7" rx="1.4" fill={color} />
      <text
        x="11" y="17.8" fontSize="5.4" fontWeight="700" fill="#fff"
        textAnchor="middle" fontFamily="Inter, Arial, sans-serif" letterSpacing="0.3"
      >{ext}</text>
    </svg>
  );
}

// Main ViewFeatured component
const ViewFeatured = (props: ViewFeaturedProps) => {
  useSeo({ title: "Signal Detail", path: "/view-featured", noindex: true });
  const [viewType, setViewType] = useState<"normalized" | "percentage" | "price">("normalized");
  const [showProjection, setShowProjection] = useState(true);
  const [showAdvancedStats, setShowAdvancedStats] = useState(
    () => localStorage.getItem("dejachart-advanced-stats-open") === "1"
  );
  // Always starts collapsed — unlike Advanced Stats, this isn't a "remember my
  // preference" toggle (it's the heaviest thing on the page, one Chart.js
  // instance per analog), so it shouldn't stay open just because a prior visit
  // expanded it.
  const [showMatchingCharts, setShowMatchingCharts] = useState(false);
  const {theme} = useTheme();
  const isDarkMode = theme === "dark";
  const chartRef = useRef<ChartJS<"line"> | null>(null);

  const {content, getFeaturedMatches} = props;
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const date = params.get("date");
  const unlock = params.get("unlock");

  useEffect(() => {
    // On a share-link visit (?unlock=…), (re)fetch so the backend overlays the ONE
    // unlocked signal — even when gated featured content is already cached from an
    // earlier /featured browse — unless this exact signal is already unlocked in the
    // cached payload (idempotency guard: unlocked_signal, avoids a refetch loop).
    const uTicker = (params.get("ticker") || "").toUpperCase();
    const uWindow = parseInt(params.get("window") || "20");
    const uDir = params.get("direction");
    // Bind once and null-check explicitly: repeating `content?.unlocked_signal?.` per
    // operand tripped no-unnecessary-condition, since the first comparison already
    // narrows both to non-nullish for the rest of the && chain.
    const u = content?.unlocked_signal;
    const alreadyUnlocked =
      !!u && u.ticker === uTicker && u.window === uWindow && u.direction === uDir;
    if (content && (!unlock || alreadyUnlocked)) return;
    getFeaturedMatches(date || "", unlock || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, date, unlock, getFeaturedMatches]);

  useEffect(() => {
    if (!content) return;
    const direction = params.get("direction");
    const ticker = params.get("ticker");
    const win = params.get("window");
    if (ticker && direction) {
      posthog.capture("featured_match_viewed", { ticker, direction, window: win });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  if (!content) {
    return (
      <Container className="py-5">
        <ChartSkeleton />
      </Container>
    );
  }

  const direction = params.get("direction");
  if (direction !== "up" && direction !== "down") return null;

  const ticker = params.get("ticker");
  const window = parseInt(params.get("window") || "20");

  const featuredWindowData =
    window === 10 ? content.featured10 :
      window === 20 ? content.featured20 :
        window === 65 ? content.featured65 :
          window === 130 ? content.featured130 :
            window === 260 ? content.featured260 :
              content.featured20;
  const featuredForDirection = featuredWindowData?.[direction];
  const data = featuredForDirection?.find((v: StockData) => v.query.ticker === ticker);

  // The percent (0-100) floor the daily script's strict pass actually used for this
  // window — reproduces exactly the analog set a featured signal cleared, rather than
  // the Matcher's own much-lower MATCHER_DEFAULT_SCORE (QueryMatcher.tsx). Prefer the
  // live value on the response (never drifts from daily.py, a 0-1 fraction here); fall
  // back to the mirrored constant only if that window's data hasn't loaded.
  const matcherScoreParam = featuredWindowData?.min_score !== undefined
    ? Math.round(featuredWindowData.min_score * 100)
    : PRODUCTION_MIN_SCORE[window] ?? PRODUCTION_MIN_SCORE[20];

  // The requested signal may not exist in this date's featured set: stale links
  // after a featured regeneration, tier-obfuscated lists (anonymous/free users only
  // see the top few tickers unmasked), or an archive date with no entry for this
  // ticker/direction/window. Render a friendly fallback instead of crashing (this
  // was a non-null `.find(...)!` that threw "data is undefined").
  if (!data) {
    const sentiment = direction === "up" ? "bullish" : "bearish";
    const winLabel = ({ 10: "2w", 20: "4w", 65: "3m", 130: "6m", 260: "1y" } as Record<number, string>)[window]
      ?? `${window}-day`;
    // For an anonymous viewer the signal is usually HIDDEN rather than missing — the
    // tease obfuscates every ticker past the top few, so a link arrived at from
    // elsewhere (Earnings Calendar, a share card) won't be found in their list. Show a
    // signup nudge instead of the misleading "the list may have been regenerated" copy.
    if (content.is_teased) {
      return (
        <Container className="py-5 text-center">
          <h4 className="mb-3">Sign up to see this signal</h4>
          <p className="text-muted">
            <strong>{ticker}</strong>&apos;s {winLabel} {sentiment} analog signal is hidden for
            signed-out visitors. A <strong>free</strong> account reveals every ticker across
            every window — no card required.
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Link to="/signup" className="btn btn-primary">Create a free account</Link>
            {ticker && (
              <Link to={buildDashboardUrl({ ticker, window, betaAware: true, score: matcherScoreParam })} className="btn btn-outline-primary">
                Open {ticker} in Matcher
              </Link>
            )}
          </div>
        </Container>
      );
    }

    return (
      <Container className="py-5 text-center">
        <h4 className="mb-3">Signal not found</h4>
        <p className="text-muted">
          {ticker} isn&apos;t in the featured {sentiment} signals
          for the {window}-day window{date ? ` on ${date}` : ""}. The featured list may have been
          regenerated since this link was created.
        </p>
        <div className="d-flex justify-content-center gap-3 mt-4">
          <Link to="/featured" className="btn btn-primary">Back to Featured</Link>
          {ticker && (
            <Link to={buildDashboardUrl({ ticker, window, score: matcherScoreParam })} className="btn btn-outline-primary">
              Open {ticker} in Matcher
            </Link>
          )}
        </div>
      </Container>
    );
  }

  const query = {
    label: data.query.query_title,
    symbol: data.query.ticker,
    symbol_name: data.query.ticker_name,
    // The query ticker is always an active, currently-signaled security (never
    // delisted — see get_signal_universe in the backend) — never inferred.
    delisted: false,
    origin_window: window,
    xaxis: data.query.query_dates,
    yaxis: data.query.query_data_norm,
    ohlcv: data.query.query_ohlcv,
  };

  const groups: MatchesState[] = [
    query,
    ...data.matches.map((match: Match) => ({
      label: match.match_title,
      symbol: match.match_symbol,
      symbol_name: match.match_symbol_name,
      delisted: match.delisted,
      origin_window: match.origin_window ?? window,
      score: match.match_score,
      beta: match.match_beta,
      beta_delta: match.match_beta_delta,
      xaxis: match.match_dates,
      yaxis: match.match_data_norm,
      // Truncate ohlcv to match_dates length: match_ohlcv may be extended to
      // curve_h_max rows (2× forecast_window) for multi-horizon curve calculations,
      // but the chart only needs window + forecast_window rows aligned with match_dates.
      // Extra rows cause _.zip to produce undefined-x data points and the direction
      // arrow (ohlcv[-1]) to read a price beyond the committed forecast horizon.
      ohlcv: match.match_ohlcv.slice(0, match.match_dates.length),
    })),
  ];

  // Determine which tab to return to based on window size
  const backToTab = `${direction}${window}`;

  // Get the start/end date from the query
  const startDate =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    data.query.query_dates && data.query.query_dates.length > 0
      ? formatDate(data.query.query_dates[0], 0)
      : "";
  const endDate =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    data.query.query_dates && data.query.query_dates.length > 0
      ? formatDate(data.query.query_dates[data.query.query_dates.length - 1], 0)
      : "";

  // Export chart as PNG with background matching current theme
  const exportChartAsPNG = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const canvas = chart.canvas;

      // Create a new canvas with background
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = canvas.width;
      exportCanvas.height = canvas.height;
      const ctx = exportCanvas.getContext("2d");

      if (ctx) {
        // Fill background based on current theme
        ctx.fillStyle = isDarkMode ? "#1E293B" : "#FFFFFF";
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

        // Draw the chart on top
        ctx.drawImage(canvas, 0, 0);

        const link = document.createElement("a");
        link.download = `${data.query.ticker}_pattern_chart.png`;
        link.href = exportCanvas.toDataURL("image/png");
        link.click();
      }
    }
  };

  // Export data as CSV
  const exportDataAsCSV = () => {
    const lines: string[] = [];
    const allStocks = [
      {symbol: data.query.ticker, delisted: false, score: null, dates: data.query.query_dates, prices: data.query.query_data, norms: data.query.query_data_norm},
      ...data.matches.map((m) => ({symbol: m.match_symbol, delisted: m.delisted, score: m.match_score, dates: m.match_dates, prices: m.match_data, norms: m.match_data_norm})),
    ];

    // Header info
    lines.push(`Pattern Analysis: ${data.query.ticker}`);
    lines.push(`Direction: ${direction.toUpperCase()},Window: ${window} Day`);
    lines.push(`Up Probability: ${data.fraction_up.toFixed(1)}%,Avg Up: ${data.avg_up.toFixed(2)}%,Max Up: ${data.max_up.toFixed(2)}%`);
    lines.push(`Down Probability: ${data.fraction_dn.toFixed(1)}%,Avg Down: ${data.avg_dn.toFixed(2)}%,Max Down: ${data.max_dn.toFixed(2)}%`);
    lines.push("");

    // Build header rows
    // Row 1: Symbol names
    const symbolRow = ["Day"];
    allStocks.forEach((stock, i) => {
      const label = i === 0 ? `${displaySymbol(stock.symbol, stock.delisted)} (Query)` : `${displaySymbol(stock.symbol, stock.delisted)} (Score: ${stock.score?.toFixed(3)})`;
      symbolRow.push(label, "", "");
    });
    lines.push(symbolRow.join(","));

    // Row 2: Column headers
    const headerRow = [""];
    allStocks.forEach(() => {
      headerRow.push("Date", "Price", "Normalized");
    });
    lines.push(headerRow.join(","));

    // Data rows
    const numDays = data.query.query_dates.length;
    for (let day = 0; day < numDays; day++) {
      const row: (string | number)[] = [day + 1];
      allStocks.forEach((stock) => {
        row.push(
          stock.dates[day] ?? "",
          stock.prices[day]?.toFixed(2) ?? "",
          stock.norms[day]?.toFixed(4) ?? ""
        );
      });
      lines.push(row.join(","));
    }

    const forecastDays = data.query.forecast_window;
    for (let day = numDays; day < numDays + forecastDays; day++) {
      const row: (string | number)[] = [day + 1];
      allStocks.forEach((stock) => {
        row.push(
          stock.dates[day] ?? "",
          stock.prices[day]?.toFixed(2) ?? "",
          stock.norms[day]?.toFixed(4) ?? ""
        );
      });
      lines.push(row.join(","));
    }

    const csvContent = lines.join("\n");
    const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${data.query.ticker}_pattern_analysis.csv`;
    link.click();
  };

  return (
    <Container className="pt-3 pb-5">
      <Card className="border-0 shadow-sm mb-4">
        <CardHeader>
          <div>
            <h1 className="mb-1">
              {/* Pass the featured query's START date so the Matcher reproduces the
                  exact same query window (same analogs / stats) — the backend anchors
                  the window at this date. Without it the Matcher defaults to today,
                  which diverges for historical featured dates. */}
              <Link
                to={buildDashboardUrl({
                  ticker: data.query.ticker,
                  window,
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  date: data.query.query_dates?.[0] ? data.query.query_dates[0].slice(0, 10) : undefined,
                  betaAware: true,
                  score: matcherScoreParam,
                })}
                className="stock-link"
              >{data.query.ticker_name || data.query.ticker}</Link>
              <EarningsBadge ticker={data.query.ticker} className="ms-2 align-middle" />
            </h1>

            {/* Industry + market cap + share, directly under the company name */}
            <div className="mb-2 d-flex align-items-center flex-wrap gap-2">
              <FundamentalsChip ticker={data.query.ticker} />
              <ShareButton
                color="primary"
                outline
                label="Share"
                className="vf-share-btn"
                title={`${data.query.ticker} — DejaChart`}
                path={`/api/share/featured/${data.query.ticker}?window=${window}&direction=${direction}${content.featured_as_of ? `&date=${content.featured_as_of}` : ""}`}
              />
              {/* Complements the Matcher deep link above: that reproduces THIS exact
                  historical signal (pinned date + score); this shows every window's
                  CURRENT readout for the ticker (no date pinning — /ticker only ever
                  renders today's data, see TickerTimeframes.tsx). */}
              <Link
                to={`/ticker/${encodeURIComponent(data.query.ticker)}`}
                className="btn btn-outline-secondary btn-sm"
              >
                All Timeframes
              </Link>
            </div>

            {/* Date range + direction + window — one line */}
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="text-muted small me-1">
                {startDate && endDate ? `${startDate} – ${endDate}` : data.query.query_title}
              </span>
              <Badge
                color={direction === "up" ? "success" : "danger"}
                className="px-2 py-1"
              >
                {direction === "up" ? "BULLISH" : "BEARISH"}
              </Badge>
              <Badge color="light" className="text-dark px-2 py-1">
                {window} Day
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardBody className="p-2 p-md-4">
          <div className="mb-3 d-flex justify-content-between align-items-center gap-2">
            <div className="d-flex align-items-center gap-2">
              <ButtonGroup size="sm">
                <Button
                  color={viewType === "normalized" ? "primary" : "light"}
                  className="vf-mini-btn"
                  onClick={() => setViewType("normalized")}
                >
                  Normalized
                </Button>
                <Button
                  color={viewType === "percentage" ? "primary" : "light"}
                  className="vf-mini-btn"
                  onClick={() => setViewType("percentage")}
                >
                  % Change
                </Button>
                <Button
                  color={viewType === "price" ? "primary" : "light"}
                  className="vf-mini-btn"
                  onClick={() => setViewType("price")}
                  title="Show actual dollar prices — analogs may trade at very different price levels than the primary, so shapes are harder to compare here than in Normalized/% Change"
                >
                  Actual Price
                </Button>
              </ButtonGroup>
              <Button
                size="sm"
                className="vf-mini-btn"
                color={showProjection ? "primary" : "light"}
                active={showProjection}
                onClick={() => setShowProjection((v) => !v)}
                title="Project the primary security forward through the forecast window using the analogs' outcomes"
              >
                Projection
              </Button>
            </div>
            <div className="d-flex align-items-center">
              <span className="text-muted me-2 small">Export:</span>
              <ButtonGroup size="sm" className="vf-export-group">
                <Button color="outline-secondary" className="vf-mini-btn" onClick={exportChartAsPNG} title="Export chart as PNG">
                  <FileTypeIcon ext="PNG" color="#3B82F6" />
                </Button>
                <Button color="outline-secondary" className="vf-mini-btn" onClick={exportDataAsCSV} title="Export data as CSV">
                  <FileTypeIcon ext="CSV" color="#059669" />
                </Button>
              </ButtonGroup>
            </div>
          </div>

          <StockChartGroup
            groups={groups}
            label={`${data.query.ticker}: ${startDate} - ${endDate}`}
            window={data.query.window}
            forecastWindow={data.query.forecast_window}
            viewType={viewType}
            isDarkMode={isDarkMode}
            showProjection={showProjection}
            chartRef={chartRef}
          />

          <div className="mt-4">
            <div className="row">
              <div className="col-12">
                <Card className="border">
                  <CardHeader className="bg-light">
                    <h5 className="mb-0">Expected Movement</h5>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-end mb-1">
                        <div>
                          <small className="text-success fw-bold">{data.fraction_up.toFixed(1)}% Bullish</small>
                        </div>
                        <small className="text-muted">Historical Direction</small>
                        <div>
                          <small className="text-danger fw-bold">{data.fraction_dn.toFixed(1)}% Bearish</small>
                        </div>
                      </div>
                      <div className="direction-bar">
                        <div
                          className="direction-bar-fill bg-success"
                          style={{width: `${data.fraction_up}%`}}
                        ></div>
                      </div>
                      <div className="text-center mt-1">
                        <small className="text-muted">
                          Of {data.count_up + data.count_dn} similar patterns, {data.count_up} went up and {data.count_dn} went down
                        </small>
                      </div>
                    </div>
                    <div className="d-flex justify-content-around text-center">
                      <div>
                        <div className="chart-stat-value">
                          <span className="text-success">
                            +{data.avg_up.toFixed(1)}%
                          </span>
                        </div>
                        <div className="small text-muted">Avg Upside</div>
                      </div>
                      <div>
                        <div className="chart-stat-value">
                          <span className="text-danger">-{data.avg_dn.toFixed(1)}%</span>
                        </div>
                        <div className="small text-muted">Avg Downside</div>
                      </div>
                      <div>
                        <div className="chart-stat-value">
                          <span
                            className={
                              direction === "up" ? "text-success" : "text-danger"
                            }
                          >
                            {direction === "up"
                              ? `+${data.avg_all.toFixed(1)}%`
                              : `${data.avg_all.toFixed(1)}%`
                            }
                          </span>
                        </div>
                        <div className="small text-muted">Expected Return</div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>

            <Card className="border mt-4">
              <CardHeader
                className="bg-light d-flex justify-content-between align-items-center"
                style={{cursor: "pointer"}}
                onClick={() =>
                  setShowAdvancedStats((v) => {
                    const next = !v;
                    localStorage.setItem("dejachart-advanced-stats-open", next ? "1" : "0");
                    return next;
                  })
                }
              >
                <h5 className="mb-0">Advanced Stats</h5>
                <span className="text-muted small">{showAdvancedStats ? "Hide ▲" : "Show ▼"}</span>
              </CardHeader>
              {showAdvancedStats && (
                <CardBody className="p-0">
                  {/* Desktop: full 12-column table (scrolls horizontally if needed) */}
                  <div className="d-none d-md-block">
                    <Table responsive bordered size="sm" className="mb-0" style={{ minWidth: 760, fontSize: 13 }}>
                      <thead>
                        <tr className="bg-light">
                          <InfoHeader
                            label="Matches"
                            tooltip="How many similar historical patterns went up vs down after the pattern completed."
                            id="matches-info"
                            colSpan={2}
                          />
                          <InfoHeader
                            label="Max Move"
                            tooltip="The single best gain and worst loss seen across all matched patterns - shows the range of possible outcomes."
                            id="max-info"
                            colSpan={2}
                          />
                          <InfoHeader
                            label="Strength Ratio"
                            tooltip="Compares bullish vs bearish momentum. Values above 1 favor that direction. Capped at 5 to limit outlier impact."
                            id="strength-info"
                            colSpan={2}
                          />
                          <InfoHeader
                            label="Movement Score"
                            tooltip="Combines probability with magnitude: (% of matches in direction) × (avg move). Higher means more consistent, larger moves."
                            id="score-info"
                            colSpan={2}
                          />
                          <InfoHeader
                            label="Risk-Adjusted Ratio"
                            tooltip="Measures potential gains relative to downside risk, similar to a Sortino ratio. Higher values indicate better risk/reward."
                            id="ratio-info"
                            colSpan={2}
                          />
                          <InfoHeader
                            label="Total Score"
                            tooltip="Overall ranking score used to select featured patterns. Combines probability, magnitude, and risk-adjustment into one metric."
                            id="total-info"
                            colSpan={2}
                          />
                        </tr>
                        <tr>
                          <th className="text-center">Up</th>
                          <th className="text-center">Down</th>
                          <th className="text-center">Up</th>
                          <th className="text-center">Down</th>
                          <th className="text-center">Up</th>
                          <th className="text-center">Down</th>
                          <th className="text-center">Up</th>
                          <th className="text-center">Down</th>
                          <th className="text-center">Upside</th>
                          <th className="text-center">Downside</th>
                          <th className="text-center">Upside</th>
                          <th className="text-center">Downside</th>
                        </tr>
                      </thead>
                      <tbody>
                        <StockStats data={data} />
                      </tbody>
                    </Table>
                  </div>
                  {/* Mobile: transposed so every metric is visible without side-scrolling */}
                  <div className="d-md-none p-2">
                    <StockStatsStacked data={data} />
                  </div>
                </CardBody>
              )}
            </Card>
          </div>

          {/* Per-analog small multiples, each on its own auto-scaled $ axis (mirrors the
              Matcher page's match grid — StockChartDisplay.tsx). The overlay chart above
              normalizes every line onto one shared scale so shapes can be compared
              directly, which flattens out each analog's own price character and makes
              distinct patterns look visually similar; these give every analog its own
              axis back so day-to-day movement reads clearly again. Collapsed by default,
              same pattern as Advanced Stats below — the grid is the heaviest thing on the
              page (one Chart.js instance per analog), so keep it out of the initial render
              until a user asks for it. */}
          {groups.length > 1 && (
            <Card className="border mt-4">
              <CardHeader
                className="bg-light d-flex justify-content-between align-items-center"
                style={{cursor: "pointer"}}
                onClick={() => setShowMatchingCharts((v) => !v)}
              >
                <h5 className="mb-0">{groups.length - 1} Matching Charts</h5>
                <span className="text-muted small">{showMatchingCharts ? "Hide ▲" : "Show ▼"}</span>
              </CardHeader>
              {showMatchingCharts && (
                <CardBody>
                  <div className="chart-grid">
                    {groups.slice(1).map((g, i) => {
                      const windowIndex = data.query.window - 1;
                      const resolutionDay = data.query.forecast_window === 0 ? 5 : data.query.forecast_window;
                      const closes = g.ohlcv.map((o) => Number(o[4]));
                      const startPrice = closes[windowIndex];
                      const endPrice = closes[windowIndex + resolutionDay];
                      const pctChange = ((endPrice - startPrice) / startPrice) * 100;
                      const isPositive = pctChange >= 0;

                      return (
                        <div
                          className="col-sm-6 col-lg-4 chart-grid-item"
                          key={`${g.symbol}-${i}`}
                        >
                          <Card className="match-chart-card">
                            <CardHeader className="match-chart-header">
                              <div className="match-chart-title">
                                <span className="match-symbol">
                                  {displaySymbol(g.symbol, g.delisted)}
                                </span>
                                <span className="match-date">
                                  <span className="match-date-label">Start:</span> {formatDate(g.label, 1)}
                                </span>
                              </div>
                              <EarningsBadge ticker={g.symbol} className="mb-1" />
                              {Number.isFinite(pctChange) && (
                                <div className="percent-change">
                                  <span className={isPositive ? "up-indicator" : "down-indicator"}>
                                    {isPositive ? "▲" : "▼"} {Math.abs(pctChange).toFixed(2)}%
                                  </span>
                                </div>
                              )}
                              {g.score !== undefined && (
                                <div className="match-score">
                                  <div className="score-bar">
                                    <div
                                      className="score-fill"
                                      style={{width: `${(g.score * 100).toFixed(0)}%`}}
                                    ></div>
                                  </div>
                                  <span className="score-value">{(g.score * 100).toFixed(0)}%</span>
                                </div>
                              )}
                              {g.beta !== undefined && (
                                <div
                                  className="beta-chip"
                                  title={`Trailing market beta ${g.beta.toFixed(2)}${
                                    g.beta_delta !== undefined
                                      ? ` (${g.beta_delta >= 0 ? "+" : ""}${g.beta_delta.toFixed(2)} vs ${data.query.ticker})`
                                      : ""
                                  }`}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 4,
                                    marginTop: 4,
                                    padding: "1px 7px",
                                    borderRadius: 10,
                                    fontSize: "0.72rem",
                                    fontVariantNumeric: "tabular-nums",
                                    border: "1px solid var(--bs-border-color, #d0d0d0)",
                                    color: "var(--bs-secondary-color, #666)",
                                  }}
                                >
                                  <span style={{fontWeight: 600}}>β&nbsp;{g.beta.toFixed(2)}</span>
                                  {g.beta_delta !== undefined && (
                                    <span
                                      style={{
                                        color:
                                      Math.abs(g.beta_delta) <= 0.5
                                        ? "var(--bs-success, #2e7d32)"
                                        : "var(--bs-warning-text, #b26a00)",
                                      }}
                                    >
                                      {g.beta_delta >= 0 ? "+" : ""}
                                      {g.beta_delta.toFixed(2)}
                                    </span>
                                  )}
                                </div>
                              )}
                            </CardHeader>
                            <CardBody className="match-chart-body">
                              <ModernStockChart
                                data={{
                                  label: g.label,
                                  window: data.query.window,
                                  symbol: displaySymbol(g.symbol, g.delisted),
                                  xaxis: g.xaxis,
                                  yaxis: closes,
                                }}
                                isCompact={true}
                              />
                            </CardBody>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              )}
            </Card>
          )}
        </CardBody>
      </Card>

      <div>
        <Link to={`/featured?tab=${backToTab}`} className="btn btn-outline-primary btn-sm">
          <i className="fas fa-arrow-left me-2"></i> Back to Featured
        </Link>
      </div>
    </Container>
  );
};

export default ViewFeatured;
